// Re-Issue Single Pass — superadmin, club-scoped.
//
// Re-issues one membership pass, identified by EITHER a member ID or a card
// number (radio), always accompanied by a mobile number.
//
// POST /frontend/reissue-single-pass
//   { issuer_id, input_type: 'member_id',    memberId, mobile }
//   { issuer_id, input_type: 'card_number',  cardId,   mobile }
// Only the key for the selected input_type is sent.
//
// Responses: 200 { error: 0, message } and 207 { error: 1, message }. Axios
// RESOLVES 207 (default validateStatus is 200-299), so the failure arrives in
// .then, not .catch — the body's error flag is the only thing separating the
// two. Same trap as ReIssuePassCreation.js and PromotionMemberMap.js.
import { React, useState, useContext } from 'react';
import {
    Button, Grid, TextField, Typography, FormControl, RadioGroup,
    FormControlLabel, Radio,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useHistory } from 'react-router-dom';
import { useUrlContext } from '../context/urlContext';
import { useAuth } from '../context/authContext';
import { errorContext } from '../Components/ErrorDisplay';
import { normalizeMobileInput, resolveDialCode } from '../Components/mobileUtils';

// A card number is exactly 10 digits. A member ID is free-form alphanumeric
// (plus - and _), matching the rule already used by GrantSingleMemberForm.
const CARD_NUMBER_RE = /^\d{10}$/;
const MEMBER_ID_RE = /^[A-Za-z0-9_-]+$/;

export default function ReIssueSinglePass() {
    const history = useHistory();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const { handleError } = useContext(errorContext);

    const [message, setMessage] = useState(null);
    const [isErrorMessage, setIsErrorMessage] = useState(false);
    // What was actually submitted. On success the inputs are cleared, so this
    // keeps the result attached to the member/card it applied to — same reason
    // ReIssuePassCreation.js retains the uploaded filename.
    const [submittedSummary, setSubmittedSummary] = useState('');

    const validationSchema = yup.object({
        input_type: yup.string().required('Please select an input type'),
        identifier: yup
            .string()
            .required('Value is required')
            .test('identifier-by-type', function (value) {
                const trimmed = (value || '').trim();
                if (!trimmed) {
                    return this.createError({ message: 'Value is required' });
                }
                if (this.parent.input_type === 'card_number') {
                    return CARD_NUMBER_RE.test(trimmed)
                        ? true
                        : this.createError({ message: 'Card number must be exactly 10 digits' });
                }
                return MEMBER_ID_RE.test(trimmed)
                    ? true
                    : this.createError({ message: 'Enter a valid Member ID' });
            }),
        // Mobile is compulsory for both input types.
        mobile: yup
            .string()
            .required('Mobile number is required')
            .test('valid-mobile', 'Enter a valid mobile number', function (value) {
                const code = resolveDialCode(this.parent.mobile_country_code);
                return normalizeMobileInput(value || '', code) !== null;
            }),
        mobile_country_code: yup.string().required('Please select country'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        validateOnMount: true,
        validationSchema,
        initialValues: {
            input_type: 'member_id',
            identifier: '',
            mobile: '',
            mobile_country_code: '61',
        },
        onSubmit: async (values) => {
            const identifier = values.identifier.trim();
            const normalized = normalizeMobileInput(
                values.mobile, resolveDialCode(values.mobile_country_code),
            );
            if (!normalized) {
                formik.setFieldTouched('mobile', true, false);
                formik.setFieldError('mobile', 'Enter a valid mobile number');
                return;
            }

            // Send only the key matching the selected input type.
            const payload = {
                issuer_id: user.issuerId,
                input_type: values.input_type,
                mobile: normalized.fullNumber,
            };
            if (values.input_type === 'card_number') {
                payload.cardId = identifier;
            } else {
                payload.memberId = identifier;
            }

            const summary = `${values.input_type === 'card_number' ? 'Card number' : 'Member ID'} `
                + `${identifier} · ${normalized.fullNumber}`;

            try {
                const response = await axios.post(UrlHelperObj.reIssueSinglePassUrl(), payload);
                const data = response.data || {};
                // 207 lands here, not in catch — check the body's error flag.
                const failed = Number(data.error) === 1;
                setIsErrorMessage(failed);
                setMessage(data.message
                    || (failed ? 'Pass could not be re-issued.' : 'Pass re-issued successfully.'));
                setSubmittedSummary(summary);
                // Clear the inputs only on success. Leaving valid data on screen
                // with Submit live invites a second click that re-issues the same
                // pass twice. On failure the values stay so they can be corrected
                // and retried without retyping.
                if (!failed) {
                    formik.resetForm();
                }
            } catch (err) {
                setIsErrorMessage(true);
                setMessage(err?.response?.data?.message || '');
                setSubmittedSummary(summary);
                handleError(err);
            }
        },
    });

    // A previous result stops being meaningful once the user edits anything.
    // Done from the change handlers rather than a useEffect on values, so the
    // programmatic resetForm() on success doesn't wipe the success message.
    function clearMessage() {
        if (message !== null) {
            setMessage(null);
            setIsErrorMessage(false);
            setSubmittedSummary('');
        }
    }

    // Switching type clears the field — a 10-digit card number and a member ID
    // are not interchangeable, so carrying the value over is never useful.
    function handleInputTypeChange(e) {
        clearMessage();
        formik.setFieldValue('input_type', e.target.value, false);
        formik.setFieldValue('identifier', '', false);
        formik.setFieldTouched('identifier', false, false);
    }

    // Manual reset — back to an empty form with no result showing.
    function handleClear() {
        formik.resetForm();
        setMessage(null);
        setIsErrorMessage(false);
        setSubmittedSummary('');
    }

    function onCancelForm() {
        history.push(user?.issuerId ? `/club/${user.issuerId}/home` : '/');
    }

    const isCardNumber = formik.values.input_type === 'card_number';
    const isSubmitDisabled = !formik.isValid || formik.isSubmitting;

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container item xs={12} spacing={2} direction="column" alignItems="flex-start"
                style={{ marginTop: 0, marginLeft: 10 }}>
                <Grid item xs={12} style={{ width: '100%' }}>
                    <Typography
                        variant={isSmallScreen ? 'h6' : 'h5'}
                        component="h1"
                        style={{
                            textAlign: 'left', marginTop: 0, marginBottom: '12px',
                            fontWeight: 400, whiteSpace: 'normal', wordBreak: 'break-word',
                            lineHeight: 1.3,
                        }}
                    >
                        Re-Issue Single Membership Pass
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            row={!isSmallScreen}
                            id="input_type"
                            name="input_type"
                            value={formik.values.input_type}
                            onChange={handleInputTypeChange}
                            onBlur={formik.handleBlur}
                        >
                            <FormControlLabel value="member_id" control={<Radio color="primary" />} label="Enter member ID" />
                            <FormControlLabel value="card_number" control={<Radio color="primary" />} label="Enter card number" />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        id="identifier"
                        name="identifier"
                        label={isCardNumber ? 'Card number' : 'Member ID'}
                        placeholder={isCardNumber ? '10 digits, e.g. 2001234568' : 'e.g. 20012345687'}
                        value={formik.values.identifier}
                        onBlur={formik.handleBlur}
                        onChange={(e) => { clearMessage(); formik.handleChange(e); }}
                        inputProps={isCardNumber ? { maxLength: 10, inputMode: 'numeric' } : undefined}
                        error={formik.touched.identifier && Boolean(formik.errors.identifier)}
                        helperText={(formik.touched.identifier && formik.errors.identifier)
                            || (isCardNumber ? 'Card number must be exactly 10 digits' : '')}
                    />
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    <PhoneInput
                        inputProps={{ name: 'mobile', required: true }}
                        required
                        value={formik.values.mobile}
                        countryCodeEditable={false}
                        containerStyle={{ width: '100%' }}
                        inputStyle={{ width: '100%', height: '56px' }}
                        copyNumbersOnly={false}
                        country={{ '1': 'us', '61': 'au', '91': 'in' }[resolveDialCode(formik.values.mobile_country_code)]}
                        onlyCountries={['au', 'in', 'us']}
                        onChange={(phone, ctr) => {
                            clearMessage();
                            const nextCountryCode = resolveDialCode(ctr?.dialCode || formik.values.mobile_country_code);
                            formik.setFieldValue('mobile_country_code', nextCountryCode, false);
                            formik.setFieldValue('mobile', phone || '', true);
                            formik.setFieldTouched('mobile', true, false);
                        }}
                        onBlur={() => formik.setFieldTouched('mobile', true)}
                    />
                    <Typography variant="caption" color="error">
                        {formik.touched.mobile && formik.errors.mobile ? formik.errors.mobile : ''}
                    </Typography>
                </Grid>
            </Grid>

            {message !== null && message !== '' && (
                <Grid container item xs={12}>
                    <Grid item xs={12} style={{ textAlign: 'left', marginTop: '10px', marginLeft: 10 }}>
                        <Typography color={isErrorMessage ? 'error' : 'primary'}>{message}</Typography>
                        {/* The inputs are cleared on success, so name what the
                            result applied to. */}
                        {submittedSummary ? (
                            <Typography variant="caption" color="text.secondary" style={{ display: 'block' }}>
                                {submittedSummary}
                            </Typography>
                        ) : null}
                    </Grid>
                </Grid>
            )}

            <Grid container item xs={12}
                direction={isSmallScreen ? 'column' : 'row'}
                spacing={isSmallScreen ? 1 : 3}
                style={{ marginLeft: 10, marginTop: 4 }}
                justifyContent="flex-start"
            >
                <Grid item>
                    <Button
                        type="submit"
                        disabled={isSubmitDisabled}
                        variant="contained"
                        style={{
                            minWidth: isSmallScreen ? 200 : 'auto',
                            backgroundColor: isSubmitDisabled ? '#9e9e9e' : '#1976d2',
                            color: isSubmitDisabled ? '#424242' : '#ffffff',
                            opacity: 1,
                            boxShadow: isSubmitDisabled ? 'none' : undefined,
                            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                            filter: isSubmitDisabled ? 'grayscale(30%)' : 'none',
                        }}
                    >
                        {formik.isSubmitting ? 'Submitting…' : 'Submit'}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        type="button"
                        style={{ minWidth: isSmallScreen ? 200 : 'auto' }}
                        onClick={handleClear}
                        variant="outlined"
                        disabled={formik.isSubmitting}
                    >
                        Clear
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        type="button"
                        style={{ minWidth: isSmallScreen ? 200 : 'auto' }}
                        onClick={onCancelForm}
                        color="secondary"
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
}
